<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, nextTick, ref, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { useRoute, useRouter } from "vue-router";

import { ModalName } from '@/constants/modals';
import { useAuthStore } from "@/modules/auth/store/authStore";
import workerService from "@/modules/worker/services/workerService";

import Loader from "@/dsComponents/Loader.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";

import { type FollowingEvent, groupEventsByDate } from "@/components/following";
import { useWorkerActivityFeed } from "@/components/following/composables/useWorkerActivityFeed";
import FollowingList from "@/components/following/FollowingList.vue";
import {
    FollowingFilterCategory,
    getFollowingFilterCategory,
} from "@/components/following/helpers/filterCategory";
import { ProfileNoteModalMode } from "@/components/modal/Profile/note/types";
import ProfilPreviewFollowingHeader from "@/components/modal/Profile/previewProfile/ProfilPreviewFollowingHeader.vue";
import { type AgencyOption, FollowingCategoryType } from "@/components/modal/Profile/previewProfile/types";

const props = withDefaults(
    defineProps<{
        workerId?: string;
        agencies?: AgencyOption[];
    }>(),
    {
        workerId: undefined,
        agencies: () => [],
    }
);

const workerId = computed(() => props.workerId);
const scrollRootEl = ref<HTMLElement | null>(null);

const modalStore = useModalStore();
const authStore = useAuthStore();
const { currentUserId } = storeToRefs(authStore);
const route = useRoute();
const router = useRouter();
const selectedAgencyIds = ref<string[]>([]);
const selectedCategoryIds = ref<FollowingCategoryType[]>([]);

const PREVIEW_CATEGORY_TO_FILTER_KEY: Record<FollowingCategoryType, FollowingFilterCategory> = {
    [FollowingCategoryType.Needs]: FollowingFilterCategory.Besoin,
    [FollowingCategoryType.Missions]: FollowingFilterCategory.Mission,
    [FollowingCategoryType.Candidatures]: FollowingFilterCategory.Candidature,
    [FollowingCategoryType.Modifications]: FollowingFilterCategory.Modification,
    [FollowingCategoryType.Contact]: FollowingFilterCategory.Contact,
    [FollowingCategoryType.Notes]: FollowingFilterCategory.Note,
    [FollowingCategoryType.Interviews]: FollowingFilterCategory.Interview,
};

const handleInterviewEditClick = (payload: { interviewId: string; summary: string }) => {
    const id = workerId.value;
    if (!id) return;
    modalStore.actions.open(ModalName.InterviewEditModal, {
        workerId: id,
        interviewId: payload.interviewId,
        initialSummary: payload.summary,
        onOptimistic: {
            update: (summary: string) => updateEventContent(payload.interviewId, summary),
            rollback: () => updateEventContent(payload.interviewId, payload.summary),
        },
    });
};

const handleFollowingLinkNavigate = (to: RouteLocationRaw, _event: MouseEvent) => {
    modalStore.actions.close(ModalName.ProfilPreviewModal);
    const resolvedTarget = router.resolve(to);
    if (resolvedTarget.fullPath !== route.fullPath) {
        router.push(to);
    }
};

const handleViewPendingProfileChanges = async () => {
    const id = workerId.value;
    if (!id) return;
    modalStore.actions.close(ModalName.ProfilPreviewModal);
    try {
        const fullWorker = await workerService.getWorker(id);
        modalStore.actions.open(ModalName.WorkerAskedChangesModal, {
            worker: fullWorker,
            workerId: id,
        });
    } catch {
        return;
    }
};

const {
    events: apiEvents,
    isInitialLoading: activityLoading,
    isLoadingMore: activityLoadingMore,
    hasNextPage,
    // biome-ignore lint/correctness/noUnusedVariables: Biome Init
    sentinelEl,
    updateEventContent,
} = useWorkerActivityFeed({
    workerId,
    scrollRoot: scrollRootEl,
    getMappingOptions: () => ({
        onViewPendingProfileChanges: handleViewPendingProfileChanges,
        workerId: workerId.value,
    }),
});

const openProfileNoteModal = (payload: {
    mode: ProfileNoteModalMode;
    initialContent: string;
    noteId?: string;
    eventId: string;
}) => {
    const id = workerId.value;
    if (!id) return;
    const modalPayload = {
        mode: payload.mode,
        workerId: id,
        initialContent: payload.initialContent,
        noteId: payload.noteId,
        onOptimistic: {
            update: (content: string) => updateEventContent(payload.eventId, content),
            rollback: () => updateEventContent(payload.eventId, payload.initialContent),
        },
    };
    const wasNoteModalOpen = modalStore.state.open.includes(ModalName.ProfileNoteModal);
    if (wasNoteModalOpen) {
        modalStore.actions.close(ModalName.ProfileNoteModal);
        nextTick(() => {
            modalStore.actions.open(ModalName.ProfileNoteModal, modalPayload);
        });
        return;
    }
    modalStore.actions.open(ModalName.ProfileNoteModal, modalPayload);
};

const handleNoteLineClick = (linePayload: { initialContent: string; noteId?: string; eventId: string }) => {
    openProfileNoteModal({
        mode: ProfileNoteModalMode.EDIT,
        initialContent: linePayload.initialContent,
        noteId: linePayload.noteId,
        eventId: linePayload.eventId,
    });
};

watch(
    () => props.workerId,
    () => {
        selectedAgencyIds.value = [];
        selectedCategoryIds.value = [];
    }
);

const filteredEvents = computed(() => {
    let result: FollowingEvent[] = apiEvents.value !== null ? apiEvents.value : [];
    if (selectedCategoryIds.value.length > 0) {
        const allowed = new Set(selectedCategoryIds.value.map((category) => PREVIEW_CATEGORY_TO_FILTER_KEY[category]));
        result = result.filter((event) => allowed.has(getFollowingFilterCategory(event)));
    }
    if (selectedAgencyIds.value.length > 0) {
        result = result.filter((event) => event.agencyId && selectedAgencyIds.value.includes(event.agencyId));
    }
    return result;
});

const sections = computed(() => groupEventsByDate(filteredEvents.value));
</script>

<template>
    <div class="profil-preview-following-tab">
        <div class="profil-preview-following-tab__header">
            <ProfilPreviewFollowingHeader
                :agencies="agencies"
                v-model:selected-agency-ids="selectedAgencyIds"
                v-model:selected-category-ids="selectedCategoryIds"
            />
        </div>
        <div ref="scrollRootEl" class="profil-preview-following-tab__body">
            <div v-if="activityLoading" class="profil-preview-following-tab__loading">
                <Loader size="md" />
            </div>
            <div v-else class="profil-preview-following-tab__sub-body">
                <FollowingList
                    :sections="sections"
                    :current-collaborator-id="currentUserId"
                    :on-link-navigate="handleFollowingLinkNavigate"
                    :on-note-line-click="workerId ? handleNoteLineClick : undefined"
                    :can-edit-interviews="authStore.isAgency"
                    :on-interview-edit-click="handleInterviewEditClick"
                />
                <div
                    v-if="hasNextPage"
                    ref="sentinelEl"
                    class="profil-preview-following-tab__sentinel"
                    aria-hidden="true"
                />
                <div v-if="activityLoadingMore" class="profil-preview-following-tab__loading-more">
                    <Loader size="sm" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.profil-preview-following-tab {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: var(--spacing-lg, 12px);
    min-height: 0;
    flex: 1;

    &__header {
        flex-shrink: 0;
        padding: var(--spacing-3xl, 24px) var(--spacing-3xl, 24px) 0 var(--spacing-3xl, 24px);
    }

    &__body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: var(--spacing-lg, 12px) var(--spacing-lg, 12px) var(--spacing-3xl, 24px) 0;
    }

    &__sub-body {
        padding: 0 var(--spacing-lg, 12px);
    }

    &__loading {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        min-height: 120px;
    }

    &__sentinel {
        width: 100%;
        height: var(--spacing-xs, 4px);
        flex-shrink: 0;
    }

    &__loading-more {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--spacing-xl, 16px) 0;
    }
}
</style>
